import { TypedDocumentNode, WatchQueryFetchPolicy } from '@apollo/client';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client/react';
import {
  Mutation,
  MutationAssignAccessGrantsToDoorsArgs,
  MutationUnassignAccessGrantsFromDoorsArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { ASSIGN_ACCESS_GRANTS_TO_DOORS, UNASSIGN_ACCESS_GRANTS_FROM_DOORS } from 'graphql/mutations';
import { GET_VISITOR_GROUPS_FOR_TENANT } from 'graphql/queries';
import { filterGroupsForUser } from 'hooks/shared/filterGroupsForUser';
import { useEffect } from 'react';
import { setVisitorsGroupsAction, updateAccessGrantsAction } from 'state/actions/visitorsGroups';
import { VisitorsGroupsState } from 'state/types';
import { authVar, visitorsGroupsVar } from 'state/vars';
import { BaseHookProps } from '../shared/types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_VISITOR_GROUPS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getVisitorGroupsForTenant'>> =
  GET_VISITOR_GROUPS_FOR_TENANT;
const ASSIGN_ACCESS_GRANTS_TO_DOORS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'assignAccessGrantsToDoors'>,
  MutationAssignAccessGrantsToDoorsArgs
> = ASSIGN_ACCESS_GRANTS_TO_DOORS;
const UNASSIGN_ACCESS_GRANTS_FROM_DOORS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'unassignAccessGrantsFromDoors'>,
  MutationUnassignAccessGrantsFromDoorsArgs
> = UNASSIGN_ACCESS_GRANTS_FROM_DOORS;

export const getVisitorGroupsIdsAssignedForDoor = (visitorsGroups: VisitorsGroupsState, doorId: string): string[] =>
  visitorsGroups
    .filter((usersGroup) =>
      usersGroup.accessGrants.some((accessGrant) => accessGrant.doors.some(({ id }) => id === doorId))
    )
    .map(({ id }) => id);

export const getDefaultAccessGrantsIdsAssignedForDoor = (
  visitorsGroups: VisitorsGroupsState,
  doorId: string
): string[] =>
  visitorsGroups
    .filter((usersGroup) =>
      usersGroup.accessGrants.some((accessGrant) => accessGrant.doors.some(({ id }) => id === doorId))
    )
    .map(({ accessGrants }) => accessGrants[0].id);

export const getDoorsIdsAssignedForVisitorGroup = (visitorsGroups: VisitorsGroupsState, visitorGroupId: string) => {
  const index = visitorsGroups.findIndex(({ id }) => id === visitorGroupId);
  const arrOfAllIds: string[] = [];
  visitorsGroups[index].accessGrants.forEach((accessGrant) =>
    accessGrant.doors.forEach((door) => arrOfAllIds.push(door.id))
  );
  return Array.from(new Set(arrOfAllIds));
};

interface Props extends BaseHookProps {
  fetchPolicy?: WatchQueryFetchPolicy | undefined;
}

const useVisitorsGroups = ({ handleFetchError, fetchPolicy = 'cache-first' }: Props) => {
  const isAdditionalLogsEnabled = process.env.REACT_APP_IS_ADDITIONAL_LOGS_ENABLED === 'true';
  const { user } = useReactiveVar(authVar);
  const visitorsGroups = useReactiveVar(visitorsGroupsVar);

  const [getVisitorGroupsForTenant, { loading }] = useLazyQuery(GET_VISITOR_GROUPS_FOR_TENANT_TYPED, {
    fetchPolicy
  });

  const [assignAccessGrantsToDoorsMutation, { loading: assignAccessGrantsToDoorsMutationLoading }] = useMutation(
    ASSIGN_ACCESS_GRANTS_TO_DOORS_TYPED,
    {
      onCompleted: (data) => {
        data.assignAccessGrantsToDoors.forEach((item) => updateAccessGrantsAction(item));
      },
      onError: () => {
        handleFetchError('Error while adding access');
      }
    }
  );
  const [unassignAccessGrantsFromDoorsMutation, { loading: unassignAccessGrantsFromDoorsMutationLoading }] =
    useMutation(UNASSIGN_ACCESS_GRANTS_FROM_DOORS_TYPED, {
      onCompleted: (data) => {
        data.unassignAccessGrantsFromDoors.forEach((item) => updateAccessGrantsAction(item));
      },
      onError: () => {
        handleFetchError('Error while deleting access');
      }
    });

  useEffect(() => {
    const fetchVisitorGroupsForTenant = async () => {
      try {
        const { data } = await getVisitorGroupsForTenant();
        if (data) {
          const groups = filterGroupsForUser(data.getVisitorGroupsForTenant, user);
          if (isAdditionalLogsEnabled) console.log(groups);
          setVisitorsGroupsAction(groups);
        }
      } catch (err) {
        if (isAdditionalLogsEnabled) console.log(err);
        handleFetchError('Error while fetching visitor groups');
      }
    };
    void fetchVisitorGroupsForTenant();
  }, [getVisitorGroupsForTenant]);

  const assignAccessGrantsToDoors = async (doorIds: string[], accessGrantIds: string[]) => {
    const { data } = await assignAccessGrantsToDoorsMutation({
      variables: {
        doorIds,
        accessGrantIds
      }
    });
    return data;
  };

  const unassignAccessGrantsFromDoors = async (doorIds: string[], accessGrantIds: string[]) => {
    const { data } = await unassignAccessGrantsFromDoorsMutation({
      variables: {
        doorIds,
        accessGrantIds
      }
    });
    return data;
  };

  return {
    visitorsGroups,
    assignAccessGrantsToDoors,
    unassignAccessGrantsFromDoors,
    getDoorsIdsAssignedForVisitorGroup,
    getVisitorGroupsIdsAssignedForDoor,
    getDefaultAccessGrantsIdsAssignedForDoor,
    loading,
    assignAccessGrantsToDoorsMutationLoading,
    unassignAccessGrantsFromDoorsMutationLoading
  };
};

export default useVisitorsGroups;
